import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DecisionKindEnum } from "@/enum/decision-kind";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatToBRL } from "@/utils/formatToMoney";
import { AlertTriangle, Undo2 } from "lucide-react";
import { useState } from "react";

import {
  DECISION_REASON_MIN_LENGTH,
  MAX_COMMENT_LENGTH,
  MIN_INSTALLMENTS,
} from "../constants";
import { isDiscountableTotal } from "../installments";
import { PAYROLL_VOUCHER_LABELS } from "../messages";
import { PayrollVoucher, VoucherDecision } from "../types";
import {
  consequenceOf,
  installmentAmountsOf,
  installmentsOf,
} from "../utils/decision-consequence";

export const DECISION_CONSEQUENCE_TEST_ID: string = "voucher-consequence";

export const DECISION_INSTALLMENTS_TEST_ID: string = "voucher-installments";

export const DECISION_REASON_TEST_ID: string = "voucher-reason";

export const DECISION_BLOCKED_TEST_ID: string = "voucher-blocked";

const DECISION_REASON_FIELD_ID: string = "voucher-reason-field";

const DECISION_REASON_HINT_ID: string = "voucher-reason-hint";

interface DecisionConfirmationModalProps {
  voucher: PayrollVoucher;
  kind: DecisionKindEnum;
  onConfirm: (decision: VoucherDecision) => Promise<void>;
}

const CONFIRM_LABEL: Record<DecisionKindEnum, string> = {
  [DecisionKindEnum.APPROVE]: PAYROLL_VOUCHER_LABELS.CONFIRM_APPROVE,
  [DecisionKindEnum.REJECT]: PAYROLL_VOUCHER_LABELS.CONFIRM_REJECT,
};

export function DecisionConfirmationModal({
  voucher,
  kind,
  onConfirm,
}: DecisionConfirmationModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [comment, setComment] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [showReasonError, setShowReasonError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isApproval: boolean = kind === DecisionKindEnum.APPROVE;

  const isBlocked: boolean =
    isApproval && !isDiscountableTotal(voucher.amount);

  const trimmedReason: string = reason.trim();
  const reasonIsValid: boolean =
    isApproval || trimmedReason.length >= DECISION_REASON_MIN_LENGTH;

  const installments: number = installmentsOf(voucher);
  const amounts: number[] = installmentAmountsOf(voucher);

  const closeModal = (): void => {
    handleModal({ isOpen: false });
  };

  const confirm = async (): Promise<void> => {
    if (isBlocked || isSubmitting) return;

    if (!reasonIsValid) {
      setShowReasonError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirm({
        kind,
        comment: isApproval ? comment.trim() : "",
        rejection_reason: isApproval ? "" : trimmedReason,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center space-y-6">
      <div
        className={`flex h-16 w-16 items-center justify-center rounded-full ${
          isApproval ? "bg-yellow-100" : "bg-gray-100"
        }`}
      >
        {isApproval ? (
          <AlertTriangle className="h-8 w-8 text-yellow-600" />
        ) : (
          <Undo2 className="h-8 w-8 text-gray-600" />
        )}
      </div>

      {isBlocked ? (
        <p
          className="w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-center text-sm text-amber-800"
          data-testid={DECISION_BLOCKED_TEST_ID}
        >
          {PAYROLL_VOUCHER_LABELS.NOT_DISCOUNTABLE}
        </p>
      ) : (
        <p
          className="text-center text-sm text-gray-700"
          data-testid={DECISION_CONSEQUENCE_TEST_ID}
        >
          {consequenceOf(kind, voucher)}
        </p>
      )}

      {isApproval && !isBlocked && (
        <div className="w-full space-y-2" data-testid={DECISION_INSTALLMENTS_TEST_ID}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {PAYROLL_VOUCHER_LABELS.INSTALLMENTS}
            </span>
            <span className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-800">
              {`${installments}x`}
            </span>
          </div>

          <p className="text-xs text-gray-500">
            {PAYROLL_VOUCHER_LABELS.INSTALLMENTS_PLANNED_BY_MANAGER}
          </p>

          {installments > MIN_INSTALLMENTS && (
            <div className="grid grid-cols-2 gap-2">
              {amounts.map((value: number, index: number) => (
                <div
                  key={`${voucher.id}-${index}`}
                  className="flex items-center gap-1 text-xs text-gray-600"
                >
                  <span className="w-8 text-gray-500">{`P${index + 1}`}</span>
                  <span className="font-medium">
                    {formatToBRL(Math.abs(value))}
                  </span>
                </div>
              ))}
            </div>
          )}

          <Textarea
            aria-label={PAYROLL_VOUCHER_LABELS.COMMENT_PLACEHOLDER}
            placeholder={PAYROLL_VOUCHER_LABELS.COMMENT_PLACEHOLDER}
            maxLength={MAX_COMMENT_LENGTH}
            value={comment}
            onChange={event => setComment(event.target.value)}
            disabled={isSubmitting}
            className="min-h-[60px] text-sm"
          />
        </div>
      )}

      {!isApproval && (
        <div className="w-full space-y-1">
          <label
            htmlFor={DECISION_REASON_FIELD_ID}
            className="text-sm font-medium text-gray-700"
          >
            {PAYROLL_VOUCHER_LABELS.REJECTION_REASON_LABEL}
          </label>
          <Textarea
            id={DECISION_REASON_FIELD_ID}
            data-testid={DECISION_REASON_TEST_ID}
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder={PAYROLL_VOUCHER_LABELS.REJECTION_REASON_PLACEHOLDER}
            maxLength={MAX_COMMENT_LENGTH}
            disabled={isSubmitting}
            aria-invalid={showReasonError && !reasonIsValid}
            aria-describedby={DECISION_REASON_HINT_ID}
            rows={3}
          />
          <p
            id={DECISION_REASON_HINT_ID}
            className={`text-xs ${
              showReasonError && !reasonIsValid
                ? "text-red-600"
                : "text-gray-500"
            }`}
          >
            {showReasonError && !reasonIsValid
              ? PAYROLL_VOUCHER_LABELS.REJECTION_REASON_TOO_SHORT(
                  DECISION_REASON_MIN_LENGTH,
                )
              : PAYROLL_VOUCHER_LABELS.REJECTION_REASON_HINT(
                  DECISION_REASON_MIN_LENGTH,
                  MAX_COMMENT_LENGTH,
                )}
          </p>
        </div>
      )}

      <div className="flex w-full space-x-3">
        <Button
          variant="outline"
          onClick={closeModal}
          disabled={isSubmitting}
          className="flex-1"
        >
          {PAYROLL_VOUCHER_LABELS.CANCEL}
        </Button>
        <Button
          onClick={confirm}
          disabled={isSubmitting || isBlocked}
          className="flex-1"
          variant={isApproval ? "default" : "destructive"}
        >
          {isSubmitting
            ? PAYROLL_VOUCHER_LABELS.PROCESSING
            : CONFIRM_LABEL[kind]}
        </Button>
      </div>
    </div>
  );
}
